/* ============================================
   AnnotationLayer — Text Selection & Highlight
   Captures text selections in wiki content,
   shows popover for creating annotations.
   Uses Selection API for range capture.
   ============================================ */

import { useState, useEffect, useCallback, type RefObject } from 'react'
import type { WikiAnotacion } from '@/hooks/useWikiAnnotations'

/* ── Annotation Colors ── */
const ANNOTATION_COLORS = [
  { name: 'Amarillo', value: '#fbbf24' },
  { name: 'Verde', value: '#34d399' },
  { name: 'Azul', value: '#60a5fa' },
  { name: 'Rosa', value: '#f472b6' },
]

/* ── Icons ── */
const icons = {
  highlight: (s = 16) => (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M12 20h9" /><path d="M16.5 3.5a2.121 2.121 0 013 3L7 19l-4 1 1-4L16.5 3.5z" />
    </svg>
  ),
  x: (s = 14) => (
    <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
    </svg>
  ),
}

interface AnnotationCreateData {
  texto_seleccionado: string
  selector_start: string
  offset_start: number
  selector_end: string
  offset_end: number
  nota: string | null
  color: string
}

interface AnnotationLayerProps {
  articuloId: string
  containerRef: RefObject<HTMLDivElement | null>
  anotaciones: WikiAnotacion[]
  onCreateAnnotation: (data: AnnotationCreateData) => void
  onDeleteAnnotation: (id: string) => void
}

/* ── CSS path from node to container ── */
function getCssPath(node: Node, container: HTMLElement): string {
  let current: Node | null = node.nodeType === Node.TEXT_NODE ? node.parentElement : node
  const parts: string[] = []

  while (current && current !== container && current instanceof HTMLElement) {
    const parent = current.parentElement
    if (!parent) break

    const siblings = Array.from(parent.children)
    const index = siblings.indexOf(current)
    const tag = current.tagName.toLowerCase()
    parts.unshift(`${tag}:nth-child(${index + 1})`)
    current = parent
  }

  return parts.join(' > ')
}

export default function AnnotationLayer({
  articuloId,
  containerRef,
  anotaciones,
  onCreateAnnotation,
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  onDeleteAnnotation: _onDeleteAnnotation,
}: AnnotationLayerProps) {
  const [popover, setPopover] = useState<{
    x: number
    y: number
    text: string
    selectorStart: string
    offsetStart: number
    selectorEnd: string
    offsetEnd: number
  } | null>(null)

  const [nota, setNota] = useState('')
  const [color, setColor] = useState(ANNOTATION_COLORS[0].value)

  /* ── Handle text selection ── */
  const handleMouseUp = useCallback(() => {
    const container = containerRef.current
    if (!container) return

    const selection = window.getSelection()
    if (!selection || selection.isCollapsed || !selection.rangeCount) return

    const range = selection.getRangeAt(0)
    const text = selection.toString().trim()
    if (!text || text.length < 3) return

    // Check if selection is inside our container
    if (!container.contains(range.commonAncestorContainer)) return

    // Get position for popover
    const rect = range.getBoundingClientRect()
    const containerRect = container.getBoundingClientRect()

    const x = rect.left - containerRect.left + rect.width / 2
    const y = rect.top - containerRect.top - 10

    // Capture selection anchors
    const selectorStart = getCssPath(range.startContainer, container)
    const selectorEnd = getCssPath(range.endContainer, container)

    setPopover({
      x,
      y,
      text,
      selectorStart,
      offsetStart: range.startOffset,
      selectorEnd,
      offsetEnd: range.endOffset,
    })
    setNota('')
    setColor(ANNOTATION_COLORS[0].value)
  }, [containerRef])

  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    container.addEventListener('mouseup', handleMouseUp)
    return () => container.removeEventListener('mouseup', handleMouseUp)
  }, [containerRef, handleMouseUp])

  /* ── Close popover ── */
  const closePopover = () => {
    setPopover(null)
    window.getSelection()?.removeAllRanges()
  }

  /* ── Save annotation ── */
  const handleSave = () => {
    if (!popover) return

    onCreateAnnotation({
      texto_seleccionado: popover.text,
      selector_start: popover.selectorStart,
      offset_start: popover.offsetStart,
      selector_end: popover.selectorEnd,
      offset_end: popover.offsetEnd,
      nota: nota.trim() || null,
      color,
    })

    closePopover()
  }

  /* ── Apply highlights to content ── */
  useEffect(() => {
    const container = containerRef.current
    if (!container) return

    // Remove existing highlights
    container.querySelectorAll('mark[data-annotation-id]').forEach(mark => {
      const parent = mark.parentNode
      if (parent) {
        while (mark.firstChild) {
          parent.insertBefore(mark.firstChild, mark)
        }
        parent.removeChild(mark)
        parent.normalize()
      }
    })

    // Apply highlights for each annotation
    for (const annotation of anotaciones) {
      try {
        // Simple text search approach - find and wrap matching text
        const walker = document.createTreeWalker(
          container,
          NodeFilter.SHOW_TEXT,
          null,
        )

        let node: Text | null
        let found = false

        while ((node = walker.nextNode() as Text | null) && !found) {
          const text = node.textContent || ''
          const idx = text.indexOf(annotation.texto_seleccionado)

          if (idx !== -1) {
            const range = document.createRange()
            range.setStart(node, idx)
            range.setEnd(node, idx + annotation.texto_seleccionado.length)

            const mark = document.createElement('mark')
            mark.setAttribute('data-annotation-id', annotation.id)
            mark.className = 'wiki-highlight'
            mark.style.backgroundColor = `${annotation.color}30`
            mark.style.borderBottom = `2px solid ${annotation.color}`
            mark.style.cursor = 'pointer'
            mark.title = annotation.nota || annotation.texto_seleccionado

            range.surroundContents(mark)
            found = true
          }
        }
      } catch {
        // Silently skip annotations that can't be applied
        // (e.g., content has changed since annotation was created)
      }
    }
  }, [anotaciones, containerRef, articuloId])

  return (
    <>
      {/* ── Selection Popover ── */}
      {popover && (
        <div
          className="wiki-annotation-popover"
          style={{
            left: Math.max(10, Math.min(popover.x - 140, 280)),
            top: popover.y,
            transform: 'translateY(-100%)',
          }}
        >
          <div className="wiki-annotation-popover__header">
            <span className="wiki-annotation-popover__title">
              {icons.highlight()} Anotar selección
            </span>
            <button
              className="wiki-annotation-popover__close"
              onClick={closePopover}
            >
              {icons.x()}
            </button>
          </div>

          <div className="wiki-annotation-popover__selected">
            "{popover.text.length > 60 ? popover.text.substring(0, 60) + '...' : popover.text}"
          </div>

          <textarea
            className="wiki-annotation-popover__textarea"
            value={nota}
            onChange={e => setNota(e.target.value)}
            placeholder="Agregar nota (opcional)..."
            rows={2}
          />

          <div className="wiki-annotation-popover__colors">
            {ANNOTATION_COLORS.map(c => (
              <button
                key={c.value}
                className={`wiki-annotation-popover__color ${color === c.value ? 'wiki-annotation-popover__color--active' : ''}`}
                style={{ background: c.value }}
                onClick={() => setColor(c.value)}
                title={c.name}
              />
            ))}
          </div>

          <div className="wiki-annotation-popover__actions">
            <button className="btn btn-secondary btn-sm" onClick={closePopover}>
              Cancelar
            </button>
            <button className="btn btn-primary btn-sm" onClick={handleSave}>
              Guardar
            </button>
          </div>
        </div>
      )}
    </>
  )
}
